import { Module } from 'vuex'
import { Client, markets as marketsApi } from '@/api'
import type { Market } from '@/market'
import {
  activeMarketApiWebUrl,
  activeMarketClientUrl,
  defaultMarketCode,
  LEGACY_NORWAY_MARKET,
  multiMarketIsEnabled,
  normalizeMarketCode,
  persistMarketCookie,
  readMarketCookie,
  resolveMarketSelection,
  validateMarkets,
} from '@/market'

export interface MarketState {
  markets: Market[];
  activeCode: string | null;
  loading: boolean;
  ready: boolean;
  error: string | null;
  fallbackApplied: boolean;
}

export const market: Module<MarketState, {}> = {
  namespaced: true,

  state: {
    markets: [],
    activeCode: null,
    loading: false,
    ready: false,
    error: null,
    fallbackApplied: false,
  },

  getters: {
    enabled: () => multiMarketIsEnabled(),
    markets: state => state.markets,
    activeCode: state => state.activeCode,
    activeMarket: state => state.markets.find(item => item.code === state.activeCode),
    defaultCurrency: state => state.markets.find(item => item.code === state.activeCode)?.default_currency,
    allowedCurrencies: state => state.markets.find(item => item.code === state.activeCode)?.allowed_currencies || [],
    loyaltyEnabled: state => state.markets.find(item => item.code === state.activeCode)?.loyalty_enabled === true,
    loading: state => state.loading,
    ready: state => state.ready,
    error: state => state.error,
    fallbackApplied: state => state.fallbackApplied,
    clientUrl: state => activeMarketClientUrl(state.activeCode || defaultMarketCode()),
    apiWebUrl: state => activeMarketApiWebUrl(state.activeCode || defaultMarketCode()),
  },

  mutations: {
    SET_LOADING(state, value: boolean) {
      state.loading = value
    },
    SET_MARKETS(state, value: Market[]) {
      state.markets = value
    },
    SET_ACTIVE_CODE(state, value: string | null) {
      state.activeCode = value
    },
    SET_READY(state, value: boolean) {
      state.ready = value
    },
    SET_ERROR(state, value: string | null) {
      state.error = value
    },
    SET_FALLBACK_APPLIED(state, value: boolean) {
      state.fallbackApplied = value
    },
  },

  actions: {
    async bootstrap({ state, commit }, force = false): Promise<boolean> {
      if (!multiMarketIsEnabled()) {
        Client.clearMarketCode()
        commit('SET_MARKETS', [{ ...LEGACY_NORWAY_MARKET, allowed_currencies: ['NOK'] }])
        commit('SET_ACTIVE_CODE', 'no')
        commit('SET_ERROR', null)
        commit('SET_LOADING', false)
        commit('SET_FALLBACK_APPLIED', false)
        commit('SET_READY', true)
        return true
      }

      if (state.ready && !force) {
        return true
      }

      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      commit('SET_READY', false)
      commit('SET_FALLBACK_APPLIED', false)
      commit('SET_ACTIVE_CODE', null)
      commit('SET_MARKETS', [])
      Client.clearMarketCode()

      try {
        const response = await marketsApi.all()
        const available = validateMarkets(response.data)
        const selection = resolveMarketSelection(
          available,
          defaultMarketCode(),
          readMarketCookie(),
        )

        commit('SET_MARKETS', available)
        commit('SET_ACTIVE_CODE', selection.market.code)
        commit('SET_FALLBACK_APPLIED', selection.fallbackApplied)
        Client.setMarketCode(selection.market.code, available.map(item => item.code))
        persistMarketCookie(selection.market.code)
        commit('SET_READY', true)
        return true
      } catch (error) {
        Client.clearMarketCode()
        const message = error instanceof Error
          ? error.message
          : 'The market configuration could not be loaded.'
        commit('SET_ERROR', message)
        return false
      } finally {
        commit('SET_LOADING', false)
      }
    },

    select({ state }, code: string): boolean {
      const normalizedCode = normalizeMarketCode(code)
      const availableCodes = state.markets.map(item => item.code)

      if (!availableCodes.includes(normalizedCode)) {
        return false
      }

      // Keep rendering and requests in the current market until navigation finishes.
      // The next document installs the saved market header during bootstrap.
      persistMarketCookie(normalizedCode)
      return true
    },

    failContext({ commit }, message?: string): void {
      commit('SET_READY', false)
      commit('SET_ERROR', message || 'The selected market is incompatible with the configured API host.')
    },
  },
}
